<template>
    <div class="flow-compare-page">
        <!-- Unified Header -->
        <div class="compare-header">
            <!-- Top row: Navigation + Title + Pickers + Actions -->
            <div class="header-top">
                <div>
                    <v-btn icon variant="text" size="small" @click="goBack" class="back-btnc">
                        <v-icon size="20">mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="header-title-cp">Flow Compare</span>
                </div>

                <!-- Pickers inline -->
                <div class="header-pickers">
                    <div class="picker-card picker-left">
                        <div class="picker-badge">
                            <v-icon size="10">mdi-circle</v-icon>
                            {{ isVersionMode ? 'VERSION A' : 'BASE' }}
                            <span v-if="isVersionMode && leftFlowName" class="version-flow-name">
                                · {{ leftFlowName }}
                            </span>
                        </div>
                        <FlowVersionPicker :key="'left-' + swapCount"
                            :label="isVersionMode ? 'Select version' : 'Base Flow'"
                            :pre-selected-flow-id="leftFlowId"
                            :pre-selected-group-id="leftGroupId"
                            :version-only="isVersionMode"
                            :exclude-flow-id="rightFlowId"
                            compact @selected="onLeftSelected"
                            @cleared="onLeftCleared" />
                    </div>

                    <v-btn icon size="x-small" variant="outlined" color="primary" class="swap-btn"
                        @click="swapFlows" :disabled="!leftFlowId || !rightFlowId">
                        <v-icon size="16">mdi-swap-horizontal</v-icon>
                    </v-btn>

                    <div class="picker-card picker-right">
                        <div class="picker-badge picker-badge-right">
                            <v-icon size="10">mdi-circle</v-icon>
                            {{ isVersionMode ? 'VERSION B' : 'COMPARE' }}
                            <span v-if="isVersionMode && rightFlowName" class="version-flow-name">
                                · {{ rightFlowName }}
                            </span>
                        </div>
                        <FlowVersionPicker v-if="isVersionMode" :key="'right-ver-' + swapCount"
                            label="Select version"
                            :pre-selected-flow-id="rightFlowId"
                            :pre-selected-group-id="rightGroupId"
                            :version-only="true"
                            :exclude-flow-id="leftFlowId"
                            compact @selected="onRightSelected"
                            @cleared="onRightCleared" />
                        <FlowVersionPicker v-else :key="'right-' + swapCount" label="Compare Flow"
                            :pre-selected-flow-id="rightFlowId"
                            :pre-selected-group-id="rightGroupId" :exclude-flow-id="leftFlowId" compact
                            @selected="onRightSelected" @cleared="onRightCleared" />
                    </div>
                </div>

                <div class="header-right">
                    <v-tooltip text="Sync both canvases" location="bottom">
                        <template #activator="{ props }">
                            <v-btn v-bind="props" icon size="small" :variant="syncView ? 'flat' : 'text'"
                                :color="syncView ? 'primary' : 'default'" @click="syncView = !syncView"
                                class="action-btn" :class="{ 'sync-active': syncView }">
                                <v-icon size="18" :color="syncView ? 'white' : undefined">{{ syncView ? 'mdi-link' : 'mdi-link-off' }}</v-icon>
                            </v-btn>
                        </template>
                    </v-tooltip>
                    <v-btn icon variant="text" size="small" @click="fitBothViews" class="action-btn">
                        <v-icon size="18">mdi-fit-to-screen</v-icon>
                    </v-btn>
                </div>
            </div>

            <!-- Summary stats strip -->
            <div v-if="diffResult" class="summary-strip">
                <span class="mini-stat stat-added">
                    <v-icon size="12">mdi-plus-circle</v-icon>
                    {{ diffResult.summary.addedNodes }}
                </span>
                <span class="mini-stat stat-removed">
                    <v-icon size="12">mdi-minus-circle</v-icon>
                    {{ diffResult.summary.removedNodes }}
                </span>
                <span class="mini-stat stat-modified">
                    <v-icon size="12">mdi-pencil-circle</v-icon>
                    {{ diffResult.summary.modifiedNodes }}
                </span>
                <span class="mini-stat stat-unchanged">
                    <v-icon size="12">mdi-check-circle</v-icon>
                    {{ diffResult.summary.unchangedNodes }}
                </span>
            </div>
        </div>

        <!-- Loading overlay -->
        <v-overlay :model-value="loading" class="align-center justify-center" contained persistent>
            <v-progress-circular indeterminate size="48" color="primary" />
            <div class="mt-3 text-body-2">Loading flows...</div>
        </v-overlay>

        <!-- Main compare area -->
        <div class="compare-container" v-if="!loading">
            <!-- Left canvas -->
            <div class="compare-panel left-panel">
                <div class="panel-label left-label">
                    <span class="panel-dot panel-dot-left"></span>
                    {{ leftFlowName || 'Select a flow' }}
                    <v-chip v-if="leftVersion" size="x-small" variant="tonal" color="blue" class="ml-1">
                        v{{ leftVersion }}
                    </v-chip>
                </div>
                <div class="canvas-wrapper">
                    <VueFlow v-if="leftNodes.length > 0" ref="leftVueFlow" :nodes="leftNodes" :edges="leftEdges"
                        :default-viewport="{ zoom: 0.8 }" :min-zoom="0.1" :max-zoom="2" :pan-on-drag="[0, 2]"
                        :nodes-connectable="false" :nodes-draggable="false" :elements-selectable="false"
                        :delete-key-code="null" class="compare-canvas" @viewport-change="onLeftViewportChange"
                        @node-click="onVueFlowNodeClick('left', $event)" :fit-view-on-init="true">
                        <template #node-flow-node="props">
                            <CompareFlowNode :id="props.id" :data="props.data" :key="props.id"
                                @node-click="onNodeClick('left', $event)" />
                        </template>
                        <template #edge-flow-edge="edgeProps">
                            <FlowEdge v-bind="edgeProps" :id="edgeProps.id" :source-x="edgeProps.sourceX"
                                :source-y="edgeProps.sourceY" :target-x="edgeProps.targetX"
                                :target-y="edgeProps.targetY" :source-position="edgeProps.sourcePosition"
                                :target-position="edgeProps.targetPosition" :marker-end="edgeProps.markerEnd"
                                :style="edgeProps.style" />
                        </template>
                        <Background patternColor="rgba(59, 130, 246, 0.15)" :gap="16" />
                    </VueFlow>
                    <div v-else class="empty-canvas">
                        <v-icon size="48" color="grey-lighten-1">mdi-file-tree-outline</v-icon>
                        <div class="text-body-2 text-grey mt-2">Select a base flow</div>
                    </div>
                </div>
            </div>

            <!-- Divider -->
            <div class="compare-divider">
                <div class="divider-line"></div>
            </div>

            <!-- Right canvas -->
            <div class="compare-panel right-panel">
                <div class="panel-label right-label">
                    <span class="panel-dot panel-dot-right"></span>
                    {{ rightFlowName || 'Select a flow' }}
                    <v-chip v-if="rightVersion" size="x-small" variant="tonal" color="orange" class="ml-1">
                        v{{ rightVersion }}
                    </v-chip>
                </div>
                <div class="canvas-wrapper">
                    <VueFlow v-if="rightNodes.length > 0" ref="rightVueFlow" :nodes="rightNodes" :edges="rightEdges"
                        :default-viewport="{ zoom: 0.8 }" :min-zoom="0.1" :max-zoom="2" :pan-on-drag="[0, 2]"
                        :nodes-connectable="false" :nodes-draggable="false" :elements-selectable="false"
                        :delete-key-code="null" class="compare-canvas" @viewport-change="onRightViewportChange"
                        @node-click="onVueFlowNodeClick('right', $event)" :fit-view-on-init="true">
                        <template #node-flow-node="props">
                            <CompareFlowNode :id="props.id" :data="props.data" :key="props.id"
                                @node-click="onNodeClick('right', $event)" />
                        </template>
                        <template #edge-flow-edge="edgeProps">
                            <FlowEdge v-bind="edgeProps" :id="edgeProps.id" :source-x="edgeProps.sourceX"
                                :source-y="edgeProps.sourceY" :target-x="edgeProps.targetX"
                                :target-y="edgeProps.targetY" :source-position="edgeProps.sourcePosition"
                                :target-position="edgeProps.targetPosition" :marker-end="edgeProps.markerEnd"
                                :style="edgeProps.style" />
                        </template>
                        <Background patternColor="rgba(249, 115, 22, 0.15)" :gap="16" />
                    </VueFlow>
                    <div v-else class="empty-canvas">
                        <v-icon size="48" color="grey-lighten-1">mdi-file-tree-outline</v-icon>
                        <div class="text-body-2 text-grey mt-2">Select a compare flow</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Diff Detail Panel (bottom drawer) -->
        <transition name="slide-up">
            <div v-if="selectedDiffNode" class="diff-panel-drawer">
                <DiffDetailPanel :diff-result="selectedDiffNode.diffResult" :left-node="selectedDiffNode.leftNode"
                    :right-node="selectedDiffNode.rightNode" :diff-state="selectedDiffNode.state"
                    @close="selectedDiffNode = null" />
            </div>
        </transition>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="bottom right">
            {{ snackbar.text }}
        </v-snackbar>
    </div>
</template>

<script>
import { MarkerType, VueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { computed } from 'vue';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';


import FlowEdge from '@/components/FlowComponents/FlowEdge.vue';
import CompareFlowNode from '@/components/FlowComponents/CompareFlowNode.vue';
import FlowVersionPicker from '@/components/FlowComponents/FlowVersionPicker.vue';
import DiffDetailPanel from '@/components/FlowComponents/DiffDetailPanel.vue';

import { convertFromFlatStructure, expandNodeData } from '@/utils/flowUtils';
import { computeFlowDiff, diffNodeData } from '@/utils/flowDiffEngine';

export default {
    name: 'FlowCompare',
    inject: ['flagNav'],
    components: {
        VueFlow,
        Background,
        FlowEdge,
        CompareFlowNode,
        FlowVersionPicker,
        DiffDetailPanel,
    },
    provide() {
        return {
            // Provide empty arrays for FlowNode inject dependencies
            aiModelOptions: computed(() => []),
            flowEdges: computed(() => []),
            availableAgentTools: computed(() => []),
            isDebugMode: computed(() => false),
            debugNodeStates: computed(() => ({})),
            debugBreakpoints: computed(() => new Set()),
            toggleDebugBreakpoint: () => { },
            debugSetNextTargetId: computed(() => ''),
            debugHoveredNodeId: computed(() => ''),
            debugStatus: computed(() => 'ready'),
            // Compare-specific provides
            nodeDiffStates: computed(() => this.nodeDiffStatesMap),
        };
    },
    data() {
        return {
            loading: false,

            // Left flow
            leftFlowId: null,
            leftGroupId: null,
            leftFlowName: '',
            leftVersion: null,
            leftNodes: [],
            leftEdges: [],
            leftRawDetails: [],

            // Right flow
            rightFlowId: null,
            rightGroupId: null,
            rightFlowName: '',
            rightVersion: null,
            rightNodes: [],
            rightEdges: [],
            rightRawDetails: [],

            // Diff
            diffResult: null,
            nodeDiffStatesMap: new Map(),
            selectedDiffNode: null,

            // UI state
            syncView: true,
            isSyncing: false,
            snackbar: { show: false, text: '', color: 'success' },

            // Mode
            isVersionMode: false,
            swapCount: 0,
        };
    },
    created() {
        // Read query params for pre-selection
        const q = this.$route.query;
        this.isVersionMode = q.mode === 'version';

        if (this.isVersionMode) {
            // Version mode: rightId = current version, left = pick version to compare
            if (q.rightId) this.rightFlowId = q.rightId;
            if (q.rightGroupId) this.rightGroupId = q.rightGroupId;
            // leftGroupId will be set after loading right flow
        } else {
            // Normal mode
            if (q.leftId) this.leftFlowId = q.leftId;
            if (q.leftGroupId) this.leftGroupId = q.leftGroupId;
            if (q.rightId) this.rightFlowId = q.rightId;
            if (q.rightGroupId) this.rightGroupId = q.rightGroupId;
        }
    },
    async mounted() {
        if (this.isVersionMode && this.rightFlowId) {
            // Version mode: load current (right) first, then set left group for version picking
            await this.loadRightFlow();
            if (this.rightGroupId) {
                this.leftGroupId = this.rightGroupId;
            }
            // Share flow name to left side (same flow)
            if (this.rightFlowName) {
                this.leftFlowName = this.rightFlowName;
            }
        } else if (this.leftFlowId && this.rightFlowId) {
            // Both IDs provided → load and diff
            await this.loadBothFlows();
        } else if (this.leftFlowId && !this.rightFlowId) {
            // Only left ID → load left and pre-fill right picker with same flow group
            await this.loadLeftFlow();
            // After left loaded, auto-set rightGroupId so user can immediately pick a version
            if (this.leftGroupId) {
                this.rightGroupId = this.leftGroupId;
            }
        }
    },
    methods: {
        // ─── Flow Selection ─────────────────────
        async onLeftSelected(selection) {
            this.leftFlowId = selection.flowId;
            this.leftGroupId = selection.flowGroupId;
            this.leftFlowName = selection.flowName;
            this.leftVersion = selection.version;
            await this.loadLeftFlow();
            this.runDiff();
        },
        async onRightSelected(selection) {
            this.rightFlowId = selection.flowId;
            this.rightGroupId = selection.flowGroupId;
            this.rightFlowName = selection.flowName;
            this.rightVersion = selection.version;
            await this.loadRightFlow();
            this.runDiff();
        },
        onLeftCleared() {
            this.leftFlowId = null;
            this.leftGroupId = null;
            this.leftFlowName = '';
            this.leftVersion = null;
            this.leftNodes = [];
            this.leftEdges = [];
            this.leftRawDetails = [];
            this.clearDiff();
        },
        onRightCleared() {
            this.rightFlowId = null;
            this.rightGroupId = null;
            this.rightFlowName = '';
            this.rightVersion = null;
            this.rightNodes = [];
            this.rightEdges = [];
            this.rightRawDetails = [];
            this.clearDiff();
        },

        // ─── Swap ───────────────────────────────
        swapFlows() {
            const temp = {
                id: this.leftFlowId, groupId: this.leftGroupId,
                name: this.leftFlowName, version: this.leftVersion,
                nodes: this.leftNodes, edges: this.leftEdges, raw: this.leftRawDetails,
            };
            this.leftFlowId = this.rightFlowId;
            this.leftGroupId = this.rightGroupId;
            this.leftFlowName = this.rightFlowName;
            this.leftVersion = this.rightVersion;
            this.leftNodes = this.rightNodes;
            this.leftEdges = this.rightEdges;
            this.leftRawDetails = this.rightRawDetails;

            this.rightFlowId = temp.id;
            this.rightGroupId = temp.groupId;
            this.rightFlowName = temp.name;
            this.rightVersion = temp.version;
            this.rightNodes = temp.nodes;
            this.rightEdges = temp.edges;
            this.rightRawDetails = temp.raw;

            this.swapCount++;
            this.runDiff();
        },

        // ─── Load Flow Data ─────────────────────
        async loadBothFlows() {
            this.loading = true;
            try {
                await Promise.all([this.loadLeftFlow(), this.loadRightFlow()]);
                this.runDiff();
            } finally {
                this.loading = false;
            }
        },

        loadFlowFromAPI(flowId) {
            return new Promise((resolve, reject) => {
                this.$call.GetFlowVersionById(
                    flowId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({ success: true, data: dataResponse.data });
                        } else {
                            resolve({ success: false, message: dataResponse.message || 'Failed to load flow' });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse?.message || 'API call failed'));
                    }
                );
            });
        },

        async loadLeftFlow() {
            if (!this.leftFlowId) return;
            try {
                const resp = await this.loadFlowFromAPI(this.leftFlowId);
                if (resp.success) {
                    const flowData = resp.data;
                    this.leftFlowName = this.leftFlowName || flowData.name;
                    this.leftVersion = this.leftVersion || flowData.version;
                    this.leftRawDetails = flowData.flowDetails || [];
                    // Capture the group ID for version-based comparison
                    if (flowData.flowGroupID && !this.leftGroupId) {
                        this.leftGroupId = flowData.flowGroupID;
                    }
                    const { nodes, edges } = this.parseFlowDetails(flowData);
                    this.leftNodes = nodes;
                    this.leftEdges = edges;
                } else {
                    this.showSnackbar(resp.message, 'error');
                }
            } catch (e) {
                this.showSnackbar('Failed to load left flow: ' + e.message, 'error');
            }
        },

        async loadRightFlow() {
            if (!this.rightFlowId) return;
            try {
                const resp = await this.loadFlowFromAPI(this.rightFlowId);
                if (resp.success) {
                    const flowData = resp.data;
                    this.rightFlowName = this.rightFlowName || flowData.name;
                    this.rightVersion = this.rightVersion || flowData.version;
                    this.rightRawDetails = flowData.flowDetails || [];
                    // Capture the group ID
                    if (flowData.flowGroupID && !this.rightGroupId) {
                        this.rightGroupId = flowData.flowGroupID;
                    }
                    const { nodes, edges } = this.parseFlowDetails(flowData);
                    this.rightNodes = nodes;
                    this.rightEdges = edges;
                } else {
                    this.showSnackbar(resp.message, 'error');
                }
            } catch (e) {
                this.showSnackbar('Failed to load right flow: ' + e.message, 'error');
            }
        },

        parseFlowDetails(flowData) {
            if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                return convertFromFlatStructure(flowData.flowDetails, MarkerType.ArrowClosed);
            }
            // Fallback for old format
            return {
                nodes: flowData.nodes || [],
                edges: flowData.edges || [],
            };
        },

        // ─── Diff Engine ────────────────────────
        runDiff() {
            if (this.leftNodes.length === 0 || this.rightNodes.length === 0) {
                this.clearDiff();
                return;
            }

            this.diffResult = computeFlowDiff(
                { nodes: this.leftNodes, edges: this.leftEdges },
                { nodes: this.rightNodes, edges: this.rightEdges }
            );

            // Build nodeDiffStatesMap for CompareFlowNode injection
            const map = new Map();

            // Process matched nodes
            this.diffResult.nodeMatching.matched.forEach(({ leftId, rightId }) => {
                const diff = this.diffResult.nodeDiffs.get(leftId);
                const state = diff?.hasChanges ? 'modified' : 'unchanged';
                const changeCount = diff?.changeCount || 0;
                map.set(leftId, { state, changeCount, rightId });
                map.set(rightId, { state, changeCount, rightId });
            });

            // Left-only (removed in right)
            this.diffResult.nodeMatching.leftOnly.forEach(id => {
                map.set(id, { state: 'removed', changeCount: 0 });
            });

            // Right-only (added in right)
            this.diffResult.nodeMatching.rightOnly.forEach(id => {
                map.set(id, { state: 'added', changeCount: 0 });
            });

            this.nodeDiffStatesMap = map;
            this.selectedDiffNode = null;
        },

        clearDiff() {
            this.diffResult = null;
            this.nodeDiffStatesMap = new Map();
            this.selectedDiffNode = null;
        },

        // ─── Node Click → Diff Detail ──────────
        onVueFlowNodeClick(side, { node, event: mouseEvent }) {
            // VueFlow @node-click fires with: { node: GraphNode, event: MouseEvent }
            const nodeId = node.id;
            if (!this.diffResult) return;

            const info = this.nodeDiffStatesMap.get(nodeId);
            if (!info) return;

            const diffState = info.state;
            if (diffState === 'unchanged') {
                // Show brief info for unchanged nodes too
                this.selectedDiffNode = null;
                this.showSnackbar('This node is unchanged between versions', 'info');
                return;
            }

            if (diffState === 'modified') {
                // Find matched pair via nodeMatching
                const matchedPair = this.diffResult.nodeMatching.matched.find(
                    m => m.leftId === nodeId || m.rightId === nodeId
                );
                if (matchedPair) {
                    const leftNode = this.diffResult.leftNodeMap.get(matchedPair.leftId);
                    const rightNode = this.diffResult.rightNodeMap.get(matchedPair.rightId);
                    const diff = this.diffResult.nodeDiffs.get(matchedPair.leftId);
                    this.selectedDiffNode = {
                        state: 'modified',
                        leftNode,
                        rightNode,
                        diffResult: diff,
                    };
                }
            } else if (diffState === 'added') {
                const addedNode = this.diffResult.rightNodeMap.get(nodeId) || this.rightNodes.find(n => n.id === nodeId);
                this.selectedDiffNode = {
                    state: 'added',
                    leftNode: null,
                    rightNode: addedNode,
                    diffResult: { modified: [], leftOnly: [], rightOnly: Object.keys(addedNode?.data || {}).map(k => ({ field: k, value: addedNode.data[k] })), unchanged: [] },
                };
            } else if (diffState === 'removed') {
                const removedNode = this.diffResult.leftNodeMap.get(nodeId) || this.leftNodes.find(n => n.id === nodeId);
                this.selectedDiffNode = {
                    state: 'removed',
                    leftNode: removedNode,
                    rightNode: null,
                    diffResult: { modified: [], leftOnly: Object.keys(removedNode?.data || {}).map(k => ({ field: k, value: removedNode.data[k] })), rightOnly: [], unchanged: [] },
                };
            }
        },

        // Legacy handler from CompareFlowNode emit (fallback)
        onNodeClick(side, event) {
            const { id, data, diffState } = event;
            if (!this.diffResult || !diffState || diffState === 'unchanged') return;
            // Delegate to the main handler
            this.onVueFlowNodeClick(side, { node: { id, data } });
        },

        // ─── Viewport Sync ──────────────────────
        onLeftViewportChange(viewport) {
            if (!this.syncView || this.isSyncing) return;
            this.isSyncing = true;
            if (this.$refs.rightVueFlow) {
                this.$refs.rightVueFlow.setViewport(viewport);
            }
            this.$nextTick(() => { this.isSyncing = false; });
        },

        onRightViewportChange(viewport) {
            if (!this.syncView || this.isSyncing) return;
            this.isSyncing = true;
            if (this.$refs.leftVueFlow) {
                this.$refs.leftVueFlow.setViewport(viewport);
            }
            this.$nextTick(() => { this.isSyncing = false; });
        },

        fitBothViews() {
            if (this.$refs.leftVueFlow) this.$refs.leftVueFlow.fitView({ padding: 0.2 });
            if (this.$refs.rightVueFlow) this.$refs.rightVueFlow.fitView({ padding: 0.2 });
        },

        // ─── Navigation ─────────────────────────
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
            } else {
                this.$router.push({ name: 'FlowList' });
            }
        },

        // ─── Helpers ────────────────────────────
        showSnackbar(text, color = 'success') {
            this.snackbar = { show: true, text, color };
        },
    }
};
</script>

<style scoped>
.flow-compare-page {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 64px);
    max-height: calc(100vh - 64px);
    top: -32px;
    background: #f0f2f5;
    overflow: hidden;
    position: relative;
}

/* ─── Unified Header ──────────────────────── */
.compare-header {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex: none;
    z-index: 10;
}

.header-top {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    padding: 8px 12px;
    gap: 8px;
}


.back-btnc {
    color: #64748b !important;
    flex-shrink: 0;
}

.back-btnc:hover {
    color: #334155 !important;
    background: #f1f5f9 !important;
}

.header-title-cp {
    font-size: 1rem;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 2px;
    justify-content: flex-end;
}

.action-btn {
    color: #64748b !important;
    border-radius: 50% !important;
}

.action-btn:hover {
    background: #f1f5f9 !important;
    color: #334155 !important;
}

.sync-active {
    border-radius: 50% !important;
}

/* ─── Pickers (inline in top row) ──────────── */
.header-pickers {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.picker-card {
    position: relative;
    flex: 1;
    min-width: 280px;
    max-width: 420px;
    padding: 4px 12px 6px 12px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #fafbfc;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.picker-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.picker-left {
    border-left: 3px solid #3b82f6;
}

.picker-right {
    border-left: 3px solid #f59e0b;
}

.picker-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.55rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: #3b82f6;
    text-transform: uppercase;
    margin-bottom: 2px;
}

.picker-badge-right {
    color: #f59e0b;
}

.swap-btn {
    flex-shrink: 0;
    border-radius: 50% !important;
    border: 1.5px solid #3b82f6 !important;
    transition: transform 0.2s, box-shadow 0.2s;
}

.swap-btn:hover:not(:disabled) {
    transform: rotate(180deg);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.version-flow-name {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: #475569;
    font-size: 0.65rem;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ─── Summary Strip ──────────────────────── */
.summary-strip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 3px 16px;
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;
}

.mini-stat {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.7rem;
    font-weight: 700;
    cursor: default;
}

.stat-added { color: #16a34a; }
.stat-removed { color: #dc2626; }
.stat-modified { color: #d97706; }
.stat-unchanged { color: #9ca3af; }

/* Main compare area */
.compare-container {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
}

.compare-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    position: relative;
}

.panel-label {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    font-size: 0.8rem;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0;
}

.left-label {
    background: rgba(59, 130, 246, 0.05);
    color: #2563eb;
    border-right: 1px solid #e2e8f0;
}

.panel-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
}

.panel-dot-left {
    background: #3b82f6;
}

.panel-dot-right {
    background: #f59e0b;
}

.right-label {
    background: rgba(249, 115, 22, 0.05);
    color: #ea580c;
    border-left: 1px solid #e2e8f0;
}

.canvas-wrapper {
    flex: 1;
    position: relative;
}

.compare-canvas {
    width: 100%;
    height: 100%;
}

.empty-canvas {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: #fafbfc;
}

/* Divider */
.compare-divider {
    width: 3px;
    background: #e2e8f0;
    position: relative;
    cursor: col-resize;
    flex-shrink: 0;
}

.divider-line {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(180deg, #3b82f6 0%, #f59e0b 100%);
    opacity: 0.4;
}

.compare-divider:hover .divider-line {
    opacity: 0.8;
}

/* Diff panel drawer */
.diff-panel-drawer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 20;
    padding: 12px 24px;
    max-height: 85vh;
    pointer-events: none;
}

.diff-panel-drawer > * {
    pointer-events: auto;
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

/* Snackbar */
.v-snackbar {
    z-index: 100;
}
</style>
